import { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import i18n from '../../i18n';
import { describeApiError } from '../../lib/errorMessages';

export type ToastTone = 'info' | 'success' | 'error' | 'warning';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: number;
  tone: ToastTone;
  title?: string;
  message: string;
  action?: ToastAction;
}

interface ApiErrorOptions {
  /** When the failure is a 409 conflict, a "Reload" action is offered that calls this. */
  onReload?: () => void;
}

interface ToastCtxValue {
  push: (t: Omit<Toast, 'id'>) => void;
  showApiError: (err: unknown, opts?: ApiErrorOptions) => void;
}

const ToastCtx = createContext<ToastCtxValue | null>(null);

// A 409 means the record changed under us (stale version), the last unit was taken by
// another cashier, or the quote was already converted. The HTTP status — not the code —
// is the reliable signal, but we tailor the wording per code where we can. (¶370)
// Read off the i18next instance rather than a hook: `showApiError` is handed to promise
// callbacks and effect bodies all over the app, so the copy has to resolve at call time, not at
// render time. i18n is initialised in main.tsx before the first component mounts.
function conflictMessage(code: string | undefined): string {
  switch (code) {
    case 'INSUFFICIENT_STOCK':
      return i18n.t('ui.conflict.insufficientStock');
    case 'QUOTE_ALREADY_CONVERTED':
      return i18n.t('ui.conflict.quoteAlreadyConverted');
    default:
      return i18n.t('ui.conflict.default');
  }
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((cur) => cur.filter((x) => x.id !== id));
  }, []);

  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setToasts((cur) => [...cur, { ...t, id }]);
    // Actionable toasts (e.g. conflict → "Reload") linger so the user can act on them.
    const ttl = t.action ? 12000 : 5000;
    setTimeout(() => setToasts((cur) => cur.filter((x) => x.id !== id)), ttl);
  }, []);

  // Section 22b — every API failure is translated to plain language through `describeApiError`
  // (lib/errorMessages.ts). Operators used to see the raw backend code (CREDIT_EXCEEDED,
  // NO_OPEN_POS_SESSION, …) as the headline, which reads like a crash. That catalogue is the
  // single translation point for the whole app, and the hook point if we ever localise.
  const showApiError = useCallback(
    (err: unknown, opts?: ApiErrorOptions) => {
      // Axios error shape
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const resp = (err as any)?.response;
      const data = resp?.data;
      const code = data?.code as string | undefined;
      const status = resp?.status as number | undefined;
      // 409 CONFLICT (¶370): surface a clear, actionable message and offer to reload the
      // current version so the edit can be re-applied.
      if (status === 409) {
        push({
          tone: 'warning',
          title: i18n.t('ui.versionConflict'),
          message: conflictMessage(code),
          action: opts?.onReload
            ? { label: i18n.t('ui.reload'), onClick: opts.onReload }
            : undefined,
        });
        return;
      }
      const serverMessage = data?.message ?? (err as Error)?.message;
      const { title, message } = describeApiError(code, serverMessage, status);
      push({ tone: 'error', title, message });
    },
    [push]
  );

  return (
    <ToastCtx.Provider value={{ push, showApiError }}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 w-80">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`rounded-md shadow-md border-l-4 bg-card text-card-foreground px-3 py-2 text-sm ${toneClass(t.tone)}`}
          >
            {t.title && <div className="font-semibold">{t.title}</div>}
            <div className="text-muted-foreground">{t.message}</div>
            {t.action && (
              <button
                onClick={() => {
                  t.action!.onClick();
                  dismiss(t.id);
                }}
                className="mt-1.5 text-xs font-medium rounded border px-2 py-0.5 hover:bg-accent"
              >
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

function toneClass(t: ToastTone): string {
  switch (t) {
    case 'success':
      return 'border-success';
    case 'error':
      return 'border-destructive';
    case 'warning':
      return 'border-warning';
    default:
      return 'border-border';
  }
}

export function useToast(): ToastCtxValue {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast outside ToastProvider');
  return ctx;
}
